import React, { useEffect, useMemo, useRef, useState } from "react";
import { Banknote, Loader2, X } from "lucide-react";

// Staff cash-tender confirmation dialog (Sep 2026).
//
// Mounted by `pages/RetailOrdersAdmin.jsx`, which owns the immutable
// per-order snapshot, the double-submit guard and the single call to
// `PATCH /retail/orders/{id}/mark-cash-received`. This component is
// presentation plus tender selection ONLY: nothing here marks anything
// paid. Choosing an amount merely enables the final button, and the
// server re-derives the amount due and recomputes the change when it
// commits — the change shown here is a preview, never the record.
//
// Money is handled in INTEGER CENTS throughout — the amounts and the
// change preview never touch binary floating point. The server's own
// numbers are authoritative and are what the caller reports afterwards.
//
// FAIL CLOSED. The amount due is established by the caller before a
// tender can be chosen. While it is resolving, and if it cannot be
// established at all, the tender controls are not rendered and the
// confirm button stays disabled: an amount we cannot establish must
// refuse the gesture rather than fall back to a guess.

export function formatCents(cents, currency = "usd") {
  const n = Number.isFinite(Number(cents)) ? Math.round(Number(cents)) : 0;
  const sign = n < 0 ? "-" : "";
  const abs = Math.abs(n);
  const symbol = { USD: "$", INR: "₹", EUR: "€", GBP: "£" }[String(currency || "").toUpperCase()] || "";
  const body = `${Math.floor(abs / 100)}.${String(abs % 100).padStart(2, "0")}`;
  return symbol ? `${sign}${symbol}${body}` : `${sign}${body} ${String(currency || "").toUpperCase()}`.trim();
}

// Parse an operator-typed amount ("20", "20.00", "$20", "1,000.5") into
// integer cents. Mirrors `backend/services/cash_tender.py::
// parse_tender_to_cents` — string maths, never `parseFloat * 100`, so
// "20.10" can never land on 2009 cents. Returns null for anything that
// is not a readable amount, so the caller can refuse rather than coerce.
export function parseAmountToCents(raw) {
  if (typeof raw !== "string" && typeof raw !== "number") return null;
  const cleaned = String(raw).trim().replace(/[$,\s]/g, "");
  if (!cleaned) return null;
  if (!/^\d*(\.\d{0,2})?$/.test(cleaned)) return null;
  const [whole, frac = ""] = cleaned.split(".");
  if (!whole && !frac) return null;
  const cents = Number(whole || "0") * 100 + Number((frac + "00").slice(0, 2));
  return Number.isFinite(cents) ? cents : null;
}

export default function CashTenderDialog({
  order,
  onClose,
  onConfirm,
  confirming = false,
  error = "",
  // How many more cash orders are waiting behind this one. Shown so
  // staff know the queue is not empty; the controller owns the count.
  queueRemaining = 0,
  // The caller is still establishing the authoritative amount due.
  amountLoading = false,
  // The caller could not establish it. Shown in place of the figure;
  // the tender controls stay unrendered and confirm stays disabled.
  amountError = "",
}) {
  const dueCents = Number(order?.amount_due_cents || 0);
  const currency = order?.currency || "usd";
  // Only an established amount can be tendered against.
  const amountReady = !amountLoading && !amountError
    && Number.isFinite(Number(order?.amount_due_cents));
  const [selectedCents, setSelectedCents] = useState(null);
  const [customValue, setCustomValue] = useState("");
  const [customOpen, setCustomOpen] = useState(false);
  const dialogRef = useRef(null);
  const closeRef = useRef(null);
  const customInputRef = useRef(null);
  const restoreFocusRef = useRef(null);
  // Read inside the key handler, which is bound once — a state value
  // captured there would be the one from first render forever.
  const confirmingRef = useRef(confirming);
  confirmingRef.current = confirming;

  // Reset every time a different order takes the dialog over — a tender
  // typed for the previous order must never carry across.
  useEffect(() => {
    setSelectedCents(null);
    setCustomValue("");
    setCustomOpen(false);
  }, [order?.id]);

  // Focus management: remember what had focus on MOUNT, move focus into
  // the dialog for EVERY order it shows, and put focus back on close.
  //
  // The move is keyed on `order?.id`, not on mount. One dialog instance
  // serves a QUEUE: when staff confirm the first of three cash orders the
  // same mounted dialog swaps to the next one, and with a mount-only
  // effect focus stayed wherever it was — the operator typed the next
  // tender into nothing. `restoreFocusRef` is still captured once, on
  // mount, because what we return focus to is where the operator was
  // before the dialog opened, not where they were mid-queue.
  useEffect(() => {
    restoreFocusRef.current = typeof document !== "undefined" ? document.activeElement : null;
    return () => {
      try { restoreFocusRef.current?.focus?.(); } catch (_e) { /* element gone */ }
    };
  }, []);

  useEffect(() => {
    closeRef.current?.focus?.();
  }, [order?.id]);

  // Escape closes (a dismissal — it never marks anything paid), and Tab
  // is trapped inside the dialog.
  //
  // Bound to the DIALOG ELEMENT and bubbling, not to `document` in the
  // capture phase. A capture-phase document listener consumed Escape for
  // every other dialog on the page while this one was open, and when two
  // cash dialogs could be mounted at once — the popup and the Retail
  // Orders recovery path — a single Escape fired both. There is now one
  // cash dialog at a time (see `components/CashTenderWatcher.jsx`) and
  // its key handling reaches only what is inside it.
  const onDialogKeyDown = (e) => {
    if (e.key === "Escape") {
      e.stopPropagation();
      // Never abandon an in-flight confirmation: the PATCH is already on
      // its way and the operator must see how it resolved.
      if (!confirmingRef.current) onClose?.();
      return;
    }
    if (e.key !== "Tab") return;
    const root = dialogRef.current;
    if (!root) return;
    const focusable = root.querySelectorAll(
      'button:not([disabled]), input:not([disabled]), [href], select, textarea, [tabindex]:not([tabindex="-1"])',
    );
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  const quickAmounts = useMemo(() => {
    const fromServer = Array.isArray(order?.quick_amounts) ? order.quick_amounts : [];
    // The server generates these per order (`services/cash_tender.py`),
    // already de-duplicated and already excluding anything at or below
    // the amount due. Falling back to "Exact Amount" alone is honest
    // when an older response has none — never an invented ladder.
    if (fromServer.length) return fromServer;
    return [{ kind: "exact", amount_cents: dueCents, label: "Exact Amount" }];
  }, [order?.quick_amounts, dueCents]);

  const customCents = customOpen ? parseAmountToCents(customValue) : null;
  const effectiveCents = customOpen ? customCents : selectedCents;
  const hasValidTender = amountReady && effectiveCents !== null && effectiveCents >= dueCents;
  const changeCents = hasValidTender ? effectiveCents - dueCents : null;
  const customTooSmall = customOpen && customCents !== null && customCents < dueCents;
  const customUnreadable = customOpen && customValue.trim() !== "" && customCents === null;

  const submit = () => {
    if (!hasValidTender || confirming) return;
    onConfirm?.(effectiveCents);
  };

  return (
    <div
      className="fixed inset-0 z-[80] bg-slate-900/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
      data-testid="cash-tender-overlay"
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="cash-tender-title"
        data-testid="cash-tender-dialog"
        data-order-id={order?.id || ""}
        onKeyDown={onDialogKeyDown}
        className="w-full sm:max-w-md bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl max-h-[92vh] overflow-y-auto"
      >
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-slate-200">
          <div className="min-w-0">
            <h2 id="cash-tender-title" className="text-base font-semibold text-slate-900 flex items-center gap-2">
              <Banknote className="w-4 h-4 text-emerald-600" aria-hidden="true" />
              Cash payment
            </h2>
            <div className="mt-1 text-sm text-slate-600">
              Order:{" "}
              <span className="font-mono font-semibold text-slate-900" data-testid="cash-tender-order-code">
                {order?.order_code || order?.id || "—"}
              </span>
            </div>
            {order?.customer_name && (
              <div className="text-xs text-slate-500 truncate" data-testid="cash-tender-customer">
                {order.customer_name}
              </div>
            )}
          </div>
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            disabled={confirming}
            aria-label="Dismiss without recording payment"
            data-testid="cash-tender-close"
            className="shrink-0 w-11 h-11 -mt-2 -mr-2 grid place-items-center rounded-full text-slate-400 hover:text-slate-700 hover:bg-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-5 py-4">
          <div className="rounded-xl bg-slate-50 border border-slate-200 px-4 py-3">
            <div className="flex items-baseline justify-between gap-3">
              <span className="text-xs uppercase tracking-wider text-slate-500">Cash to collect</span>
              {amountLoading ? (
                <span
                  className="text-sm text-slate-600 inline-flex items-center gap-2"
                  data-testid="cash-tender-amount-loading"
                >
                  <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
                  Checking the amount due…
                </span>
              ) : amountError ? (
                <span
                  className="text-sm font-semibold text-rose-700 text-right"
                  data-testid="cash-tender-amount-error"
                >
                  {amountError}
                </span>
              ) : (
                <span className="text-xl font-bold text-slate-900 tabular-nums" data-testid="cash-tender-amount-due">
                  {formatCents(dueCents, currency)}
                </span>
              )}
            </div>
            {/* A loyalty-reduced order is shown honestly: the full price
                AND the reduced amount staff must actually collect. */}
            {amountReady && Number(order?.order_total_cents || 0) > dueCents && (
              <div className="mt-1 flex items-baseline justify-between gap-3 text-[11px] text-slate-500">
                <span>Full order value {formatCents(order.order_total_cents, currency)}</span>
                <span>Loyalty points applied</span>
              </div>
            )}
          </div>

          {amountReady && (
          <div className="mt-4">
            <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Cash received</div>
            <div className="grid grid-cols-2 gap-2" role="group" aria-label="Quick cash amounts">
              {quickAmounts.map((q) => {
                const active = !customOpen && selectedCents === q.amount_cents;
                return (
                  <button
                    key={`${q.kind}-${q.amount_cents}`}
                    type="button"
                    onClick={() => { setCustomOpen(false); setSelectedCents(q.amount_cents); }}
                    aria-pressed={active}
                    data-testid={`cash-tender-quick-${q.amount_cents}`}
                    className={
                      "h-12 rounded-lg border text-sm font-semibold transition focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 "
                      + (active
                        ? "border-emerald-600 bg-emerald-50 text-emerald-800"
                        : "border-slate-300 bg-white text-slate-800 hover:border-emerald-400 hover:bg-emerald-50/40")
                    }
                  >
                    {q.kind === "exact" ? `Exact Amount · ${formatCents(q.amount_cents, currency)}` : q.label}
                  </button>
                );
              })}
              <button
                type="button"
                onClick={() => {
                  setCustomOpen(true);
                  setSelectedCents(null);
                  window.setTimeout(() => customInputRef.current?.focus?.(), 0);
                }}
                aria-pressed={customOpen}
                data-testid="cash-tender-other"
                className={
                  "h-12 rounded-lg border text-sm font-semibold transition focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 "
                  + (customOpen
                    ? "border-emerald-600 bg-emerald-50 text-emerald-800"
                    : "border-slate-300 bg-white text-slate-800 hover:border-emerald-400 hover:bg-emerald-50/40")
                }
              >
                Other Amount
              </button>
            </div>

            {customOpen && (
              <div className="mt-3">
                <label
                  htmlFor="cash-tender-custom-input"
                  className="block text-xs uppercase tracking-wider text-slate-500 mb-1"
                >
                  Amount received
                </label>
                <input
                  ref={customInputRef}
                  id="cash-tender-custom-input"
                  data-testid="cash-tender-custom-input"
                  value={customValue}
                  onChange={(e) => setCustomValue(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); submit(); } }}
                  inputMode="decimal"
                  placeholder="e.g. 20.00"
                  aria-describedby="cash-tender-custom-hint"
                  className="w-full h-12 rounded-lg border border-slate-300 px-3 text-base tabular-nums focus:outline-none focus:ring-2 focus:ring-emerald-400"
                />
                <p id="cash-tender-custom-hint" className="mt-1 text-[11px] text-slate-500">
                  Must be at least {formatCents(dueCents, currency)}.
                </p>
                {customUnreadable && (
                  <p className="mt-1 text-xs text-rose-600" role="alert" data-testid="cash-tender-custom-invalid">
                    Enter an amount like 20.00.
                  </p>
                )}
                {customTooSmall && (
                  <p className="mt-1 text-xs text-rose-600" role="alert" data-testid="cash-tender-custom-too-small">
                    That is less than the amount due.
                  </p>
                )}
              </div>
            )}
          </div>
          )}

          {/* Preview only. The server recomputes change when it commits,
              and the committed numbers are what the receipt prints. */}
          <div
            className="mt-4 rounded-xl border border-slate-200 px-4 py-3 text-sm"
            aria-live="polite"
            data-testid="cash-tender-change-panel"
          >
            {hasValidTender ? (
              <>
                <div className="flex items-baseline justify-between gap-3">
                  <span className="text-slate-600">Cash tendered</span>
                  <span className="font-semibold tabular-nums text-slate-900" data-testid="cash-tender-tendered">
                    {formatCents(effectiveCents, currency)}
                  </span>
                </div>
                <div className="mt-1 flex items-baseline justify-between gap-3">
                  <span className="text-slate-600">Change due</span>
                  <span className="font-bold text-lg tabular-nums text-emerald-700" data-testid="cash-tender-change">
                    {formatCents(changeCents, currency)}
                  </span>
                </div>
              </>
            ) : (
              <div className="text-slate-500" data-testid="cash-tender-change-empty">
                Choose the amount the customer handed over.
              </div>
            )}
          </div>

          <p
            className="mt-3 text-sm font-semibold text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2"
            data-testid="cash-tender-warning"
          >
            Confirming marks this cash payment as paid. Only confirm once you are
            holding the cash shown above.
          </p>

          {error && (
            <div
              role="alert"
              data-testid="cash-tender-error"
              className="mt-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700"
            >
              {error}
            </div>
          )}
        </div>

        <div className="px-5 py-4 border-t border-slate-200 flex items-center gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={confirming}
            data-testid="cash-tender-dismiss"
            className="h-12 px-4 rounded-lg border border-slate-300 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400"
          >
            Not now
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={!hasValidTender || confirming}
            data-testid="cash-tender-confirm"
            className={
              "flex-1 h-12 rounded-lg text-white text-sm font-bold uppercase tracking-wider transition focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 "
              + (!hasValidTender || confirming
                ? "bg-emerald-600/40 cursor-not-allowed"
                : "bg-emerald-600 hover:bg-emerald-700 active:scale-[0.99]")
            }
          >
            {confirming ? "Recording…" : "Cash Received"}
          </button>
        </div>

        {queueRemaining > 0 && (
          <div
            className="px-5 pb-4 -mt-2 text-[11px] text-slate-500"
            data-testid="cash-tender-queue-remaining"
          >
            {queueRemaining} more cash {queueRemaining === 1 ? "order is" : "orders are"} waiting.
          </div>
        )}
      </div>
    </div>
  );
}
